// src/components/AdminSlotList.jsx
import React, { useMemo, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

const SESSION_LABEL = {
  morning: "早診",
  afternoon: "午診",
  evening: "晚診",
};
const SESSION_OPTIONS = [
  { value: "morning", label: "早診 (08:30–12:00)" },
  { value: "afternoon", label: "午診 (14:30–17:30)" },
  { value: "evening", label: "晚診 (18:30–21:30)" },
];

// 正規化：只留有效字串，排除空字與破折號
function cleanNames(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter((s) => s && s !== "—" && s !== "-");
}

export default function AdminSlotList() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [doctor, setDoctor] = useState("");
  const [date, setDate] = useState(today);
  const [session, setSession] = useState("morning");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [capacity, setCapacity] = useState(30);
  const [error, setError] = useState("");

  // 從 Supabase roster 抓「某日」的班表
  const [rosterLoading, setRosterLoading] = useState(false);
  const [roster, setRoster] = useState(null); // {morning:[], afternoon:[], evening:[]} 或 null
  const [rosterErr, setRosterErr] = useState("");

  useEffect(() => {
    let abort = false;
    async function fetchRosterForDate() {
      setRosterLoading(true);
      setRosterErr("");
      setRoster(null);
      try {
        const { data, error } = await supabase
          .from("roster")
          .select("morning,afternoon,evening")
          .eq("date", date)
          .maybeSingle(); // 若無資料則 data=null, error=null
        if (abort) return;
        if (error) {
          console.error("[AdminSlotList] roster error:", error);
          setRosterErr("讀取班表失敗");
          setRoster(null);
        } else {
          setRoster(
            data
              ? {
                  morning: cleanNames(data.morning),
                  afternoon: cleanNames(data.afternoon),
                  evening: cleanNames(data.evening),
                }
              : null
          );
        }
      } catch (e) {
        if (!abort) {
          console.error("[AdminSlotList] roster exception:", e);
          setRosterErr("讀取班表發生例外");
          setRoster(null);
        }
      } finally {
        if (!abort) setRosterLoading(false);
      }
    }
    if (date) fetchRosterForDate();
    return () => {
      abort = true;
    };
  }, [date]);

  // 依日期＋時段，從 roster 算出可選醫師
  const doctorOptions = useMemo(() => {
    if (!roster) return [];
    return cleanNames(roster[session] || []);
  }, [roster, session]);

  // 只有一位醫師時自動帶入；若目前所選不在名單內則清空
  useEffect(() => {
    if (doctorOptions.length === 1) {
      setDoctor(doctorOptions[0]);
    } else if (doctor && !doctorOptions.includes(doctor)) {
      setDoctor("");
    }
  }, [doctorOptions, doctor]);

  const canSearch = Boolean(doctor.trim() && date && session);

  // 查詢名單 + 名額
  const fetchList = async () => {
    if (!canSearch) return;
    setLoading(true);
    setError("");
    try {
      // 名額
      const quotaRes = await supabase.rpc("api_get_quota", {
        p_doctor: doctor.trim(),
        p_date: date,
        p_session: session,
      });
      if (quotaRes.error) throw quotaRes.error;
      const quotaRow = Array.isArray(quotaRes.data)
        ? quotaRes.data[0]
        : quotaRes.data;
      setCapacity(Number(quotaRow?.capacity ?? 30));

      // 名單
      const listRes = await supabase.rpc("api_list_slot", {
        p_doctor: doctor.trim(),
        p_date: date,
        p_session: session,
      });
      if (listRes.error) throw listRes.error;
      setRows(Array.isArray(listRes.data) ? listRes.data : []);
    } catch (err) {
      console.error("[AdminSlotList] fetch error:", err);
      const code = err?.code || err?.name || "";
      const msg = err?.message || "";
      setError(`讀取失敗（${code}）${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // 匯出 CSV
  const downloadCSV = () => {
    if (!rows.length) return;
    const headers = [
      "日期",
      "時段",
      "醫師",
      "序號",
      "姓名",
      "身分證字號",
      "電話",
      "建立時間(台北)",
    ];
    const toTwDatetime = (iso) => {
      try {
        return new Date(iso).toLocaleString("zh-TW", {
          hour12: false,
          timeZone: "Asia/Taipei",
        });
      } catch {
        return iso;
      }
    };
    const data = rows.map((r, idx) => [
      date,
      SESSION_LABEL[session] || session,
      doctor.trim(),
      String(idx + 1),
      r.name || "",
      r.nid || "",
      r.phone || "",
      toTwDatetime(r.created_at),
    ]);
    const csv = [
      headers.join(","),
      ...data.map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${date}_${doctor.trim()}_${session}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const used = rows.length;
  const remaining = Math.max(capacity - used, 0);

  return (
    <div style={styles.wrap}>
      <h2 style={styles.h2}>門診預約名單管理</h2>

      {/* 第一列：查詢條件 */}
      <div style={styles.form}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>日期</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.control}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>時段</label>
          <select
            value={session}
            onChange={(e) => setSession(e.target.value)}
            style={styles.control}
          >
            {SESSION_OPTIONS.map((s) => (
              <option value={s.value} key={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>醫師</label>
          <select
            value={doctor}
            onChange={(e) => setDoctor(e.target.value)}
            style={styles.control}
            disabled={rosterLoading || doctorOptions.length === 0}
          >
            <option value="" disabled>
              {rosterLoading
                ? "班表載入中…"
                : doctorOptions.length
                ? "請選擇醫師"
                : "此日/時段無門診"}
            </option>
            {doctorOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.actions}>
          <button
            onClick={fetchList}
            disabled={!canSearch || loading}
            style={styles.button}
            title={!doctor && doctorOptions.length === 1 ? "已自動帶入" : ""}
          >
            {loading ? "讀取中…" : "查詢名單"}
          </button>
          <button
            onClick={downloadCSV}
            disabled={!rows.length}
            style={{ ...styles.button, ...styles.secondary }}
          >
            下載 CSV
          </button>
        </div>
      </div>

      {/* 班表提示 */}
      {rosterErr && (
        <div style={styles.error} role="alert">
          {rosterErr}
        </div>
      )}
      {!!doctorOptions.length && !rosterErr && (
        <div style={styles.doctorsHint}>
          本時段醫師：{doctorOptions.join("、")}
        </div>
      )}

      {/* 名額摘要 */}
      <div style={styles.summary}>
        <div>
          <strong>名額：</strong>
          {used} / {capacity}（剩餘 {remaining}）
        </div>
        <div>
          <strong>條件：</strong>
          {date}・{SESSION_LABEL[session] || session}・{doctor || "—"}
        </div>
      </div>

      {error && (
        <div style={styles.error} role="alert">
          {error}
        </div>
      )}

      {/* 名單表格 */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>序</th>
              <th style={styles.th}>姓名</th>
              <th style={styles.th}>身分證字號</th>
              <th style={styles.th}>電話</th>
              <th style={styles.th}>建立時間</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={styles.empty}>
                  尚無資料
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.id || `${r.nid}-${i}`}>
                  <td style={styles.tdCenter}>{i + 1}</td>
                  <td style={styles.td}>{r.name}</td>
                  <td style={styles.tdMono}>{r.nid}</td>
                  <td style={styles.tdMono}>{r.phone}</td>
                  <td style={styles.td}>
                    {new Date(r.created_at).toLocaleString("zh-TW", {
                      hour12: false,
                      timeZone: "Asia/Taipei",
                    })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- inline styles ---
const styles = {
  wrap: { maxWidth: 960, margin: "24px auto", padding: "0 16px" },
  h2: { margin: "0 0 16px", fontSize: 22 },
  form: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr auto auto",
    gap: 12,
    alignItems: "end",
    marginBottom: 8,
  },
  inputGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, color: "#555" },
  control: {
    height: 40,
    padding: "6px 10px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    outline: "none",
    background: "#fff",
  },
  actions: { display: "flex", gap: 8, alignItems: "center" },
  button: {
    height: 40,
    padding: "0 14px",
    borderRadius: 8,
    border: "1px solid #0ea5e9",
    background: "#0ea5e9",
    color: "#fff",
    cursor: "pointer",
  },
  secondary: { border: "1px solid #64748b", background: "#64748b" },
  doctorsHint: { fontSize: 12, color: "#6b7280", margin: "4px 0 12px" },
  summary: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 10px",
    borderRadius: 8,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    marginBottom: 8,
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    background: "#f1f5f9",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  td: { padding: "10px 12px", borderBottom: "1px solid #f1f5f9" },
  tdCenter: {
    padding: "10px 12px",
    textAlign: "center",
    borderBottom: "1px solid #f1f5f9",
    width: 48,
  },
  tdMono: {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  error: {
    color: "#b91c1c",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    padding: "8px 10px",
    borderRadius: 8,
    margin: "8px 0",
  },
  empty: { padding: "24px 0", textAlign: "center", color: "#6b7280" },
};
